import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  OfferStatus,
  OffersQueryVariables,
} from 'src/common/apollo/types/swarmx/graphql'
import useDebounceCallback from 'src/common/hooks/helpers/useDebounceCallback'
import useEffectCompare from 'src/common/hooks/helpers/useEffectCompare'
import useOnFirstMount from 'src/common/hooks/helpers/useOnFirstMount'
import { ExpandedOfferFillType, Filters } from '../filters'
import { buildOffersFilter, getOffersFilterFromSearchParams } from './utils'

export type OnFilterChange = (filters: OffersQueryVariables['filter']) => void

const useManageFilter = (
  onFilterChange: OnFilterChange,
  autoApplyFilter?: boolean
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = getOffersFilterFromSearchParams(searchParams)
  const handleFilterChange = useDebounceCallback(onFilterChange, 1000)

  useOnFirstMount(() => {
    const id = searchParams.get('id')

    onFilterChange({
      ...buildOffersFilter(filters),
      ...(id && { id }),
    })
  })

  const applyFilter = useCallback(() => {
    handleFilterChange(buildOffersFilter(filters))
  }, [filters, handleFilterChange])

  useEffectCompare(() => {
    if (autoApplyFilter) {
      applyFilter()
    }
  }, [filters])

  const updateFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) => {
      // Deletes id param for the case where the filter will change after the page is loaded via a share link
      searchParams.delete('id')
      searchParams.set(key, value.toString())
      if (!searchParams.get(key)) {
        searchParams.delete(key)
      }
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams]
  )

  const updateDepositAssetsFilter = (value: string[]) =>
    updateFilter('depositAssets', value)

  const updateWithdrawalAssetsFilter = (value: string[]) =>
    updateFilter('withdrawalAssets', value)

  const updateOfferFillTypeFilter = (value: ExpandedOfferFillType[]) =>
    updateFilter('offerStatuses', value)

  const updateTimeRangeFilter = (value: number) =>
    updateFilter('timeRange', value)

  const resetFilters = useCallback(() => {
    setSearchParams({
      offerStatuses: [OfferStatus.PartiallyTaken, OfferStatus.NotTaken].join(
        ','
      ),
    })
  }, [setSearchParams])

  const swapAssetFilters = () => {
    updateDepositAssetsFilter(filters.withdrawalAssets)
    updateWithdrawalAssetsFilter(filters.depositAssets)
  }

  return {
    filters,
    updateDepositAssetsFilter,
    updateWithdrawalAssetsFilter,
    updateOfferFillTypeFilter,
    updateTimeRangeFilter,
    applyFilter,
    resetFilters,
    swapAssetFilters,
  }
}

export default useManageFilter
